import { cn } from "cn";
import type { ReactNode } from "react";
import { Skeleton } from "./skeleton";

/**
 * Область загрузки: скелетон для глаза, прежняя фраза — для читалки.
 * Полосы скрыты от дерева доступности. Фраза доступна при чтении региона;
 * объявления при монтировании нет: регион вставляется в DOM сразу с текстом,
 * а `aria-live` так не работает (см. комментарий у «На связи» в
 * `density-screen.tsx`). Доменных знаний нет: форму скелетона задаёт вызывающий.
 */
export interface LoadingRegionProps {
  /** Фраза для читалки; на экране не видна. */
  label: string;
  className?: string;
  /** `aria-busy` у региона. Выключается там, где предок уже помечен занятым. */
  busy?: boolean;
  children: ReactNode;
}

export function LoadingRegion({ label, className, busy = true, children }: LoadingRegionProps) {
  return (
    <div
      role="status"
      aria-busy={busy ? "true" : undefined}
      data-slot="loading-region"
      className={className}
    >
      <span className="sr-only">{label}</span>
      <div aria-hidden="true" className="contents">
        {children}
      </div>
    </div>
  );
}

/** Ширины полос чередуются, как в примере «Text» документации shadcn. */
const LINE_WIDTHS = ["w-full", "w-3/4", "w-1/2", "w-2/3"] as const;

export interface SkeletonLinesProps {
  count: number;
  className?: string;
}

/** Строки текста: полосы `h-4` разной ширины. */
export function SkeletonLines({ count, className }: SkeletonLinesProps) {
  return (
    <div className={cn("flex flex-col gap-2", className)}>
      {Array.from({ length: count }, (_, index) => (
        <Skeleton
          // biome-ignore lint/suspicious/noArrayIndexKey: статичные декоративные полосы
          key={index}
          className={cn("h-4", LINE_WIDTHS[index % LINE_WIDTHS.length])}
        />
      ))}
    </div>
  );
}
